/**
 * Tech Rider & TBL store – single source of truth for artist tech rider and TBL.
 * Session-based fetch like personal details: ensureFetched() runs GET once per session.
 */

import { create } from "zustand";
import { ArtistService } from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";
import type {
  TechRiderTblData,
  TechRiderTblPatchPayload,
} from "@/services/artistService";

export type { TechRiderTblData, TechRiderTblPatchPayload };

interface TechRiderTblState {
  data: TechRiderTblData | null;
  isLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: TechRiderTblData | null) => void;
  fetchTechRiderTbl: () => Promise<TechRiderTblData | null>;
  /** Fetches only if !hasFetchedInSession. Call when user opens the section. */
  ensureFetched: () => Promise<TechRiderTblData | null>;
  /** PUT then GET; updates store on success. */
  putTechRiderTbl: (payload: TechRiderTblPatchPayload) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Call on logout to reset session cache. */
  reset: () => void;
}

let inFlightFetch: Promise<TechRiderTblData | null> | null = null;

const defaultData: TechRiderTblData = {
  tbl: false,
  tbl_desc: "",
  tech_rider: false,
  tech_rider_desc: "",
};

const initialState = {
  data: null,
  isLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useTechRiderTblStore = create<TechRiderTblState>()((set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchTechRiderTbl: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getTechRiderTbl();
        set({
          data,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return data;
      } catch (err) {
        set({
          error: "Failed to load tech rider & TBL",
          isLoading: false,
          data: defaultData,
        });
        return null;
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchTechRiderTbl, data } = get();
    if (hasFetchedInSession) return data;
    return fetchTechRiderTbl();
  },

  putTechRiderTbl: async (payload) => {
    set({ error: null });
    try {
      await ArtistService.putTechRiderTbl(payload);
      await get().fetchTechRiderTbl();
    } catch (err) {
      set({ error: "Failed to save. Please try again." });
      throw err;
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}));
